import { useEffect } from 'react';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters';
import type { ChildQueryControllerProps } from '@atlassian/jira-business-list-handlers';
import { ff } from '@atlassian/jira-feature-flagging';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fieldIds, LOAD_CHILD_ISSUES_EXPERIENCE } from '../../common/constants';
import { useTimelineViewIssues as useTimelineViewIssuesService } from '../../services/get-timeline-view-issues';
import type { TimelineViewIssuesReturn } from '../../services/get-timeline-view-issues/types';

type TimelineViewViewChildQueryControllerProps =
	ChildQueryControllerProps<TimelineViewIssuesReturn>;

/**
 * When this component used, it will make a request to get list of issues for a specific JQL query
 * Essentially it's acting as a proxy for some hooks, e.g. (`useTimelineViewIssuesService`)
 * It's a proxy written in component way so we can safely generate hook call as we go.
 *
 * When any result of the hooks changed, it will notify the callback using `onDataChange`.
 */
export const TimelineViewChildQueryController = ({
	parentHierarchyType,
	parentIssueId,
	onDataChange,
	parentHierarchyLevel,
}: TimelineViewViewChildQueryControllerProps) => {
	const startExperience = useExperienceStart(LOAD_CHILD_ISSUES_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(LOAD_CHILD_ISSUES_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(LOAD_CHILD_ISSUES_EXPERIENCE);
	startExperience();

	const { quickFiltersJql, jqlContext } = useFilteredViewContextInput({
		view: 'timeline',
	});

	const {
		data,
		rankIssue,
		updateInCache,
		updateItemsInCache,
		loading,
		error,
		loadNextPage,
		isLoadingNextPage,
	} = useTimelineViewIssuesService({
		input: {
			jqlContext,
			fieldIds,
			quickFiltersJql,
			parentIssue: {
				hierarchyType: parentHierarchyType,
				...(ff('jwm.visual-hierarchy-milestone-1') && {
					hierarchyLevel: parentHierarchyLevel,
				}),
				issueId: parentIssueId,
			},
		},
	});
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const totalIssues = data?.issues.length;
	useEffect(() => {
		if (loading) {
			return;
		}

		if (error || typeof totalIssues !== 'number') {
			markExperienceFailed(
				'timeline children query controller',
				error || Error('data.issues.length is not a number'),
			);
			return;
		}

		markExperienceSuccess();
		fireTrackAnalytics(createAnalyticsEvent({}), 'timelineChildrenData fetched', {
			totalChildren: totalIssues,
			parentIssueId,
			parentHierarchyLevel,
		});
	}, [
		loading,
		createAnalyticsEvent,
		totalIssues,
		parentIssueId,
		error,
		markExperienceSuccess,
		markExperienceFailed,
		parentHierarchyLevel,
	]);

	useEffect(() => {
		onDataChange(parentIssueId, {
			data,
			updateInCache,
			rankIssue,
			updateItemsInCache,
			loading,
			error,
			loadNextPage,
			isLoadingNextPage,
		});
	}, [
		data,
		error,
		isLoadingNextPage,
		loadNextPage,
		loading,
		onDataChange,
		parentIssueId,
		updateInCache,
		rankIssue,
		updateItemsInCache,
	]);

	return null;
};
